<template>
  <div
    :class="[
      'input',
      'input-phone',
      { '-has-error': errorMessage },
      { '-is-disabled': disabled },
      { '-is-focused': isFocused },
    ]">
    <label
      v-if="!!props.label"
      :for="`${props.name}-digits`"
      class="input__label input-phone__label">
      {{ computedLabel }}
    </label>
    <div class="input__wr input-phone__wr">
      <input-select-native
        v-if="countryEnabled && exists(countryOptions)"
        v-model="countryCode"
        :name="`${props.name}-prefix`"
        class="input-phone__sub input-phone__country"
        :options="parsedOptions"
        placeholder="-"
        :has-error="!!errorMessage"
        sync
        @focus="isPrefixFocused = true"
        @blur="isPrefixFocused = false" />

      <input-text
        :id="`${props.name}-digits`"
        v-model="digits"
        :outlined="true"
        :mask="maskOptions"
        :has-error="!!errorMessage"
        :focused="isDigitsFocused"
        :name="`${props.name}-digits`"
        :placeholder="placeholder"
        class="input-phone__sub input-phone__digits"
        @focus="isDigitsFocused = true"
        @blur.stop="isDigitsFocused = false" />
    </div>

    <span
      v-show="!!errorMessage"
      class="input__error input-phone__error">{{ errorMessage }}</span>
  </div>
</template>

<script setup>
  import { getCountryCallingCode } from 'libphonenumber-js/min'
  import { useField } from 'vee-validate'

  const props = defineProps({
    required: Boolean,
    name: String,
    placeholder: String,
    countryEnabled: Boolean,
    countryOptions: Array,
    label: String,
    disabled: {
      type: Boolean,
      default: false
    },
    focused: {
      type: Boolean,
      default: false
    },
    modelValue: String
  })

  const countryCode = ref()
  const digits = ref()
  const isDigitsFocused = ref(false)
  const isPrefixFocused = ref(false)

  const emit = defineEmits([
    'focus',
    'blur',
    'update:modelValue'
  ])

  const {
    value, errorMessage, handleBlur, handleChange
  } = useField(() => props.name, undefined, {
    validateOnValueUpdate: false,
    syncVModel: true
  })

  const maskOptions = {
    'data-maska': 'N',
    'data-maska-tokens': 'N:[0-9]:multiple'
  }

  const computedLabel = computed(() => {
    return `${props.label}${props.required ? '*' : ''}`
  })

  const parsedOptions = computed(() => {
    return props.countryOptions?.map(x => {
      const codePoints = x.value.split('').map(char => 127397 + char.charCodeAt(0))

      return ({
        value: x.value,
        // label: `${String.fromCodePoint(...codePoints)}`
        label: `${x.label} (+${getCountryCallingCode(x.value)})`
      })
    })
  })

  const computedPrefix = computed(() => {
    return !!countryCode.value && countryCode.value != '' ? `+${getCountryCallingCode(countryCode.value)}` : ''
  })

  const computedPhone = computed(() => {
    return `${computedPrefix.value}${digits.value ?? ''}`
  })

  const isFocused = computed(() => isDigitsFocused.value || isPrefixFocused.value)

  watch(isFocused, newState => {
    if (newState) {
      emit('focus')
    } else {
      handleBlur()
      handleChange(computedPhone.value)
      emit('blur')
    }
  })

  watch(computedPhone, newValue => {
    value.value = newValue
    if (!!errorMessage.value) {
      handleChange(newValue)
    }
  })
</script>

<style lang="scss" scoped>
.input-phone {
  @include input-main;

  position: relative;

  .input-phone__wr {
    display: flex;
    gap: 0;
  }

  .input-phone__country {
    width: 60px;
    flex: none;
    z-index: 1;
  }

  .input-phone__digits {
    flex: 1 1 0;
    min-width: 0;
  }

  :deep(.input-text .input__field) {
    padding: 10px;
  }

  .input-phone__sub {
    &.-is-focused {
      :deep(.input__wr) {
        background-color: transparent;
      }
    }

    :deep(.input__wr) {
      padding: 0;
      border: none;
      background-color: transparent;
      border-radius: 0;
    }
  }
}
</style>
